import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../../../auth/AuthProvider';
import { Money } from '../../../components/Money';
import { DiscountInput } from '../components/DiscountInput';
import { productQuery, unitOptions, type TaxRate } from '../lib/api';
import { lineNeedsDiscount, type CartLine } from '../lib/cart';
import { dec, isDecimal, mul, toPlain } from '../lib/decimal';
import { computeTotals } from '../lib/totals';
import type { PricingSettings } from '../lib/cart';
import { ItemTile } from './bits';

/** Edit one cart line: quantity, unit, price, discount and tax. */
export function LineEditDialog({
  line,
  taxRates,
  pricing,
  onSave,
  onRemove,
  onClose,
}: {
  line: CartLine;
  taxRates: TaxRate[];
  pricing: PricingSettings;
  onSave: (patch: Partial<CartLine>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const mayDiscount = can('sales.discount');
  const [draft, setDraft] = useState<CartLine>(line);
  const product = useQuery(productQuery(line.productId));
  const units = product.data ? unitOptions(product.data) : [];
  const set = (patch: Partial<CartLine>) => setDraft((d) => ({ ...d, ...patch }));

  const qtyError = !isDecimal(draft.qty) || dec(draft.qty) <= 0n;
  const priceError = !isDecimal(draft.unitPrice);
  const needsPermission = !mayDiscount && lineNeedsDiscount(draft);
  const totals = computeTotals(
    [
      {
        qty: qtyError ? '0' : draft.qty,
        unitPrice: priceError ? '0' : draft.unitPrice,
        discount: draft.discount,
        taxRate: draft.taxRate,
      },
    ],
    pricing,
  );
  const lineError = totals.errors[0];
  const invalid = qtyError || priceError || needsPermission || !!lineError;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) onSave(draft);
        }}
      >
        <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ItemTile name={line.name} size={40} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" noWrap>
              {line.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {line.sku} · list price <Money value={line.listPrice} /> / {line.unitSymbol}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Quantity"
                value={draft.qty}
                autoFocus
                onFocus={(e) => e.target.select()}
                onChange={(e) => set({ qty: e.target.value.replace(/[^\d.]/g, '') })}
                error={qtyError}
                helperText={qtyError ? 'Enter a quantity above 0' : undefined}
                slotProps={{ htmlInput: { inputMode: 'decimal', className: 'tabular' } }}
              />
              <TextField
                select
                label="Unit"
                value={draft.unitId}
                sx={{ maxWidth: 140 }}
                disabled={units.length <= 1}
                onChange={(e) => {
                  const u = units.find((x) => x.unitId === e.target.value);
                  if (!u) return;
                  const list = toPlain(mul(dec(draft.basePrice), dec(u.factor)));
                  set({
                    unitId: u.unitId,
                    unitSymbol: u.symbol,
                    factor: u.factor,
                    listPrice: list,
                    unitPrice: list,
                  });
                }}
              >
                {(units.length
                  ? units
                  : [{ unitId: line.unitId, symbol: line.unitSymbol, factor: line.factor }]
                ).map((u) => (
                  <MenuItem key={u.unitId} value={u.unitId}>
                    {u.symbol}
                    {u.factor !== '1' ? ` (×${toPlain(dec(u.factor))})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label={`Unit price (per ${draft.unitSymbol})`}
              value={draft.unitPrice}
              onFocus={(e) => e.target.select()}
              onChange={(e) => set({ unitPrice: e.target.value.replace(/[^\d.]/g, '') })}
              error={priceError}
              slotProps={{ htmlInput: { inputMode: 'decimal', className: 'tabular' } }}
            />
            <DiscountInput
              value={draft.discount}
              onChange={(discount) => set({ discount })}
              disabled={!mayDiscount}
              error={!!lineError}
              helperText={
                lineError ?? (mayDiscount ? undefined : 'You need the “Give discounts” permission')
              }
            />
            {pricing.taxEnabled && taxRates.length > 0 && (
              <TextField
                select
                label="Tax"
                value={draft.taxRateId ?? ''}
                onChange={(e) => {
                  const t = taxRates.find((r) => r.id === e.target.value);
                  set({ taxRateId: t?.id ?? null, taxRate: t?.rate ?? '0' });
                }}
              >
                <MenuItem value="">No tax</MenuItem>
                {taxRates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {needsPermission && (
              <Alert severity="warning">
                Selling below the list price needs the “Give discounts” permission.
              </Alert>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'baseline',
                p: 1.5,
                borderRadius: '12px',
                bgcolor: 'action.hover',
              }}
            >
              <Typography color="text.secondary">Line total</Typography>
              <Typography variant="h4" className="tabular">
                <Money value={totals.total} />
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
          <Button color="error" startIcon={<DeleteOutlineRounded />} onClick={onRemove}>
            Remove
          </Button>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={invalid}>
              Update
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
